import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { ChatCircleDots, X, Microphone, SpeakerHigh, SpeakerX, PaperPlaneTilt, Stop, CalendarCheck } from "@phosphor-icons/react";

const GREETING = "Hey! I'm Coach Felix's AI Fitness Concierge. Feel free to type your questions or tap the microphone icon below to speak with me directly about our Bay Area packages, pricing, or booking a call. How can I help today?";

function getSessionId() {
  let id = localStorage.getItem("existfit_chat_session");
  if (!id) {
    id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("existfit_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const sessionIdRef = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const speak = (text) => {
    if (!voiceEnabled || !("speechSynthesis" in window) || !text) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1.02;
    utter.pitch = 1;
    window.speechSynthesis.speak(utter);
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setSending(true);

    try {
      const resp = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const lines = part.split("\n");
          let eventName = "message";
          let dataLine = null;
          for (const line of lines) {
            if (line.startsWith("event: ")) eventName = line.slice(7).trim();
            else if (line.startsWith("data: ")) dataLine = line.slice(6);
          }
          if (dataLine == null || eventName === "done") continue;
          if (eventName === "cta") {
            try {
              const cta = JSON.parse(dataLine);
              setMessages((m) => [...m, { role: "cta", ...cta }]);
            } catch {}
            continue;
          }
          full += dataLine.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: full };
            return copy;
          });
        }
      }
      if (full) speak(full);
    } catch (e) {
      toast.error("Concierge is offline, please try again");
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having connection trouble. Please try again or book a free call above!" };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      send(transcript);
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const goToBooking = (url) => {
    setOpen(false);
    setTimeout(() => {
      document.querySelector(url)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.6)] hover:shadow-[0_0_45px_rgba(212,175,55,0.9)] hover:scale-105 transition-all duration-300"
      >
        {open ? <X size={28} weight="bold" /> : <ChatCircleDots size={28} weight="fill" />}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-[#141414] border border-[#D4AF37]/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A] bg-black/40">
            <div>
              <p className="font-heading text-white tracking-tight">AI Fitness Concierge</p>
              <p className="text-xs text-[#71717A]">Ask about packages, pricing & Coach Felix</p>
            </div>
            <button
              data-testid="chat-voice-toggle"
              onClick={() => setVoiceEnabled((v) => !v)}
              title={voiceEnabled ? "Mute voice replies" : "Enable voice replies"}
              className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
            >
              {voiceEnabled ? <SpeakerHigh size={20} /> : <SpeakerX size={20} />}
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) =>
              m.role === "cta" ? (
                <button
                  key={i}
                  data-testid={`chat-cta-booking-${i}`}
                  onClick={() => goToBooking(m.url)}
                  className="flex items-center gap-2 bg-[#D4AF37] text-black text-sm font-semibold rounded-full pl-4 pr-5 py-2.5 hover:bg-white hover:scale-[1.02] transition-all duration-200 shadow-[0_0_20px_rgba(212,175,55,0.4)]"
                >
                  <CalendarCheck size={18} weight="bold" /> {m.label}
                </button>
              ) : (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#D4AF37] text-black ml-auto" : "bg-[#0A0A0A] text-white border border-[#27272A]"
                  }`}
                >
                  {m.content || (sending && i === messages.length - 1 ? "..." : "")}
                </div>
              )
            )}
          </div>
          <div className="border-t border-[#27272A] p-3 flex items-center gap-2">
            <input
              data-testid="chat-text-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type or tap 🎙️ to talk..."
              className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-full px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              data-testid="chat-mic-button"
              onClick={toggleListening}
              title="Tap to speak"
              className={`p-2.5 rounded-full transition-colors ${
                listening ? "bg-red-500 text-white" : "bg-white/10 text-white hover:bg-[#D4AF37]/20"
              }`}
            >
              {listening ? <Stop size={18} /> : <Microphone size={18} />}
            </button>
            <button
              data-testid="chat-send-button"
              onClick={() => send()}
              disabled={sending}
              className="p-2.5 rounded-full bg-[#D4AF37] text-black disabled:opacity-50"
            >
              <PaperPlaneTilt size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
